'use strict';

class BuildABox extends HTMLElement {
  constructor() {
    super();
    this.min = parseInt(this.dataset.min, 10) || 1;
    this.max = parseInt(this.dataset.max, 10) || 10;
    this.currentCategory = 'nuts';
    this.currentPage = {
      nuts: 1,
      candy: 1,
      chocolate: 1
    };
    this.itemsPerPage = 12;
    this.bundleCount = 0;
    this.productIds = [];
    this.totalPrice = 0;
    this.products = {
      nuts: [],
      candy: [],
      chocolate: []
    };
    
    // Connect to existing product bundle system
    this.productBundle = null;
  }

  connectedCallback() {
    this.elements = {
      navTabs: this.querySelectorAll('.build-a-box__nav-tab'),
      categories: this.querySelectorAll('.build-a-box__category'),
      progressFill: this.querySelectorAll('[data-component="build-a-box-progress-fill"]'),
      progressCount: this.querySelectorAll('[data-component="build-a-box-count"]'),
      totalPrice: this.querySelectorAll('[data-component="build-a-box-total"]'),
      checkoutBtn: document.querySelector('[data-component="build-a-box-checkout"]'), // Use document instead of this
      checkoutMessage: this.querySelector('[data-component="build-a-box-message"]'),
      boxItems: this.querySelectorAll('.bundle-product'),
    };

    // Find and connect to existing product bundle
    this.productBundle = this.querySelector('product-bundle');
    if (this.productBundle) {
      // Wait for product bundle to be ready
      setTimeout(() => {
        this.connectToProductBundle();
      }, 100);
    }

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.loadProducts();
    this.updateProgress();
    this.updateSubmitButtonState();
  }

  connectToProductBundle() {
    if (this.productBundle) {
      // Listen for changes in the product bundle
      this.productBundle.addEventListener('bundle-updated', (e) => {
        this.syncWithProductBundle();
      });
      
      // Initial sync
      this.syncWithProductBundle();
    }
  }

  syncWithProductBundle() {
    if (!this.productBundle) return;
    
    // Check the actual bundle items in the DOM
    const bundleItems = this.querySelectorAll('.bundle-product:not([available])');
    this.bundleCount = bundleItems.length;
    
    console.log('syncWithProductBundle:', {
      bundleItemsCount: bundleItems.length,
      bundleCount: this.bundleCount,
      bundleItems: bundleItems
    });
    
    // Calculate total from actual items
    this.totalPrice = 0;
    bundleItems.forEach(item => {
      const priceEl = item.querySelector('[data-component="product-bundle-item-price"]');
      if (priceEl) {
        const priceText = priceEl.textContent || priceEl.innerText;
        const price = this.parsePrice(priceText);
        this.totalPrice += price;
      }
    });
    
    // Update our UI elements
    this.updateProgress();
    this.updateSubmitButtonState();
    this.updateTotalPrice();
  }

  handleCheckout() {
    if (this.bundleCount < 10) {
      this.showMessage('Please add 10 items to checkout');
      return;
    }

    // Get all bundle items and their data
    const bundleItems = this.querySelectorAll('.bundle-product:not([available])');
    const items = [];
    
    bundleItems.forEach(item => {
      const productId = item.dataset.productId;
      const variantId = item.dataset.currentId || item.dataset.variantId || productId;
      const quantity = 1; // Each item in bundle is quantity 1
      
      if (productId && variantId) {
        items.push({
          id: variantId,
          quantity: quantity
        });
      }
    });

    if (items.length === 0) {
      this.showMessage('No items to add to cart');
      return;
    }

    console.log('Adding items to cart:', items);
    // Add items to cart
    this.addItemsToCart(items);
  }

  async addItemsToCart(items) {
    const checkoutBtn = this.elements.checkoutBtn;
    if (checkoutBtn) {
      checkoutBtn.disabled = true;
      const loadingSpinner = checkoutBtn.querySelector('.loading-spinner');
      if (loadingSpinner) {
        loadingSpinner.style.display = 'block';
      }
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items
        })
      });

      if (response.ok) {
        // Redirect to cart
        window.location.href = '/cart';
      } else {
        throw new Error('Failed to add items to cart');
      }
    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.showMessage('Error adding items to cart. Please try again.');
    } finally {
      if (checkoutBtn) {
        checkoutBtn.disabled = false;
        const loadingSpinner = checkoutBtn.querySelector('.loading-spinner');
        if (loadingSpinner) {
          loadingSpinner.style.display = 'none';
        }
      }
    }
  }

  setupEventListeners() {
    // Navigation tabs
    this.elements.navTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const category = e.currentTarget.dataset.category;
        this.switchCategory(category);
      });
    });
    
    // Mobile sidebar toggle
    this.setupMobileSidebarToggle();


    // Listen for product bundle updates
    this.addEventListener('click', (e) => {
      if (e.target.closest('.card__button[data-product-id]') || 
          e.target.closest('[data-component="product-bundle-remove-btn"]')) {
        // Let the existing product bundle system handle this
        setTimeout(() => {
          this.syncWithProductBundle();
        }, 200);
      }
      
      // Handle checkout button click
      if (e.target.closest('[data-component="build-a-box-checkout"]')) {
        e.preventDefault();
        this.handleCheckout();
      }
    });

    // Also listen for changes in the bundle items
    const observer = new MutationObserver(() => {
      this.syncWithProductBundle();
    });
    
    const boxItems = this.querySelector('.build-a-box__box-items');
    if (boxItems) {
      observer.observe(boxItems, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['available']
      });
    }

    // Add document-level event listener for floating button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-component="build-a-box-checkout"]')) {
        e.preventDefault();
        this.handleCheckout();
      }
    });

    // Variant selection
    this.addEventListener('change', (e) => {
      if (e.target.closest('.bundle-product__select select')) {
        this.handleVariantChange(e.target);
      }
    });
  }

  async loadProducts() {
    // This would typically fetch products from the server
    // For now, we'll work with the products already rendered in the DOM
    this.extractProductsFromDOM();
    this.updatePagination();
  }

  extractProductsFromDOM() {
    this.elements.categories.forEach(category => {
      const categoryName = category.dataset.category;
      const productCards = category.querySelectorAll('.card');
      
      this.products[categoryName] = Array.from(productCards).map(card => {
        const button = card.querySelector('.card__button[data-product-id]');
        if (!button) return null;
        
        return {
          id: button.dataset.productId,
          title: button.dataset.productTitle,
          price: button.dataset.productPrice,
          image: button.dataset.productImage,
          variants: button.dataset.productVariants ? JSON.parse(button.dataset.productVariants) : [],
          element: card
        };
      }).filter(Boolean);
    });
  }

  switchCategory(category) {
    // Update active tab
    this.elements.navTabs.forEach(tab => {
      tab.classList.toggle('active', tab.dataset.category === category);
    });

    // Update active category
    this.elements.categories.forEach(cat => {
      cat.classList.toggle('active', cat.dataset.category === category);
    });

    this.currentCategory = category;
  }


  // Removed custom addProductToBundle - using existing product bundle system

  populateBundleItem(item, product) {
    const titleEl = item.querySelector('[data-component="product-bundle-item-title"]');
    const priceEl = item.querySelector('[data-component="product-bundle-item-price"]');
    const mediaEl = item.querySelector('[data-component="product-bundle-item-media"]');
    const variantsEl = item.querySelector('[data-component="product-bundle-item-variants"]');

    if (titleEl) titleEl.textContent = product.title;
    if (priceEl) priceEl.textContent = product.price;
    if (mediaEl) {
      mediaEl.innerHTML = `<img src="${product.image}" alt="${product.title}" loading="lazy">`;
    }

    // Add variants if available
    if (variantsEl && product.variants.length > 0) {
      this.addVariants(variantsEl, product.variants);
    }

    item.dataset.productId = product.id;
    item.dataset.currentId = product.id;
  }

  addVariants(variantsEl, variants) {
    if (variants.length <= 1) return;

    const select = document.createElement('select');
    select.className = 'bundle-product__select select';
    select.setAttribute('data-component', 'product-bundle-select');

    variants.forEach(variant => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      option.dataset.price = variant.price;
      if (variant.image) option.dataset.media = variant.image;
      select.appendChild(option);
    });

    variantsEl.innerHTML = '';
    variantsEl.appendChild(select);
  }

  // Removed custom removeProductFromBundle - using existing product bundle system

  clearBundleItem(item) {
    const titleEl = item.querySelector('[data-component="product-bundle-item-title"]');
    const priceEl = item.querySelector('[data-component="product-bundle-item-price"]');
    const mediaEl = item.querySelector('[data-component="product-bundle-item-media"]');
    const variantsEl = item.querySelector('[data-component="product-bundle-item-variants"]');

    if (titleEl) titleEl.textContent = '';
    if (priceEl) priceEl.textContent = '';
    if (mediaEl) mediaEl.innerHTML = '<div class="placeholder-text">Empty slot</div>';
    if (variantsEl) variantsEl.innerHTML = '';

    delete item.dataset.productId;
    delete item.dataset.currentId;
  }

  handleVariantChange(select) {
    const bundleItem = select.closest('.bundle-product');
    const selectedOption = select.options[select.selectedIndex];
    const newPrice = this.parsePrice(selectedOption.dataset.price);
    const oldPrice = bundleItem.oldPrice || 0;
    
    // Update total price
    this.totalPrice = this.totalPrice - oldPrice + newPrice;
    bundleItem.oldPrice = newPrice;
    
    // Update display
    const priceEl = bundleItem.querySelector('[data-component="product-bundle-item-price"]');
    if (priceEl) priceEl.textContent = selectedOption.dataset.price;
    
    // Update media if available
    if (selectedOption.dataset.media) {
      const mediaEl = bundleItem.querySelector('[data-component="product-bundle-item-media"] img');
      if (mediaEl) mediaEl.src = selectedOption.dataset.media;
    }
    
    this.updateTotalPrice();
    this.updateSubmitButtonState();
  }

  updateProgress() { 
    const requiredItems = 10;
    const progress = (this.bundleCount / requiredItems) * 100;
    
    console.log('updateProgress called:', {
      bundleCount: this.bundleCount,
      progress: progress,
      progressFillCount: this.elements.progressFill?.length || 0,
      progressCountTargets: this.elements.progressCount?.length || 0
    });
    
    if (this.elements.progressFill && this.elements.progressFill.length) {
      this.elements.progressFill.forEach(fill => {
        fill.style.setProperty('--progress', `${progress}%`);
      });
    }
    
    // Update ALL count elements (progress bar and collapsed header)
    const allCountElements = this.querySelectorAll('[data-component="build-a-box-count"]');
    allCountElements.forEach(el => {
      el.textContent = this.bundleCount;
    });
  }

  updateSubmitButtonState() {
    const requiredItems = 10; // Always require 10 items for checkout
    const isEnabled = this.bundleCount >= requiredItems;
    const hasItems = this.bundleCount > 0;
    
    console.log('updateSubmitButtonState:', {
      bundleCount: this.bundleCount,
      requiredItems,
      isEnabled,
      hasItems,
      checkoutBtn: this.elements.checkoutBtn
    });
    
    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.disabled = !isEnabled;
      
      // Update button classes for different states
      this.elements.checkoutBtn.classList.remove('button--building', 'button--ready');
      
      if (hasItems && !isEnabled) {
        this.elements.checkoutBtn.classList.add('button--building');
      } else if (isEnabled) {
        this.elements.checkoutBtn.classList.add('button--ready');
      }
    }
    
    if (this.elements.checkoutMessage) {
      if (isEnabled) {
        // Check if we have 10+ items AND total is over $50 for free shipping message
        const qualifiesForFreeShipping = this.bundleCount >= 10 && this.totalPrice > 50;
        
        console.log('Checkout message check:', {
          bundleCount: this.bundleCount,
          totalPrice: this.totalPrice,
          qualifiesForFreeShipping: qualifiesForFreeShipping
        });
        
        if (qualifiesForFreeShipping) {
          this.elements.checkoutMessage.textContent = 'Ready to checkout with free shipping!';
        } else {
          this.elements.checkoutMessage.textContent = 'Ready to checkout!';
        }
        this.elements.checkoutMessage.style.color = 'rgb(var(--color-accent))';
      } else if (hasItems) {
        const remaining = requiredItems - this.bundleCount;
        this.elements.checkoutMessage.textContent = `Add ${remaining} more item${remaining > 1 ? 's' : ''} to checkout`;
        this.elements.checkoutMessage.style.color = 'rgba(var(--color-foreground), 0.7)';
      } else {
        this.elements.checkoutMessage.textContent = 'Add 10 items to checkout';
        this.elements.checkoutMessage.style.color = 'rgba(var(--color-foreground), 0.6)';
      }
    }
  }

  updateTotalPrice() {
    const formattedPrice = this.formatPrice(this.totalPrice);
    this.elements.totalPrice.forEach(el => {
      const span = el.querySelector('span');
      if (span) span.textContent = formattedPrice;
    });
    
    // Also update the floating checkout button price
    const floatingCheckoutPrice = document.querySelector('.build-a-box__floating-price span');
    if (floatingCheckoutPrice) {
      floatingCheckoutPrice.textContent = formattedPrice;
    }
  }


  showAddAnimation(button) {
    const rect = button.getBoundingClientRect();
    const animation = document.createElement('div');
    animation.className = 'build-a-box__add-animation';
    animation.style.cssText = `
      position: fixed;
      left: ${rect.left + rect.width / 2}px;
      top: ${rect.top + rect.height / 2}px;
      width: 40px;
      height: 40px;
      background: rgb(var(--color-accent));
      border-radius: 50%;
      pointer-events: none;
      z-index: 1000;
      animation: flyToBox 0.8s ease-out forwards;
    `;
    
    document.body.appendChild(animation);
    
    setTimeout(() => {
      animation.remove();
    }, 800);
  }

  showMessage(message) {
    // Create or update message element
    let messageEl = this.querySelector('.build-a-box__message');
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'build-a-box__message';
      messageEl.style.cssText = `
        position: fixed;
        top: 2rem;
        right: 2rem;
        background: rgb(var(--color-accent));
        color: rgb(var(--color-background));
        padding: 1rem 2rem;
        border-radius: 0.8rem;
        font-weight: 600;
        z-index: 1000;
        animation: slideInRight 0.3s ease-out;
      `;
      document.body.appendChild(messageEl);
    }
    
    messageEl.textContent = message;
    
    setTimeout(() => {
      messageEl.style.animation = 'slideOutRight 0.3s ease-in forwards';
      setTimeout(() => messageEl.remove(), 300);
    }, 3000);
  }

  parsePrice(priceString) {
    return parseFloat(priceString.replace(/[^0-9.-]+/g, '')) || 0;
  }

  formatPrice(price) {
    return price.toFixed(2);
  }
  
  setupMobileSidebarToggle() {
    const toggleBtn = this.querySelector('[data-mobile-sidebar-toggle]');
    const boxItemsContainer = this.querySelector('[data-mobile-box-items]');
    
    if (!toggleBtn || !boxItemsContainer) return;
    
    // Initialize state - collapsed by default on mobile (chevron down, items hidden)
    // Don't set inline styles - let CSS handle it
    toggleBtn.setAttribute('aria-expanded', 'false');
    boxItemsContainer.setAttribute('data-expanded', 'false');
    
    toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
      const newState = !isExpanded;
      
      toggleBtn.setAttribute('aria-expanded', newState.toString());
      boxItemsContainer.setAttribute('data-expanded', newState.toString());
    });
  }
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes flyToBox {
    0% {
      transform: scale(1) translate(0, 0);
      opacity: 1;
    }
    50% {
      transform: scale(0.8) translate(-200px, -100px);
      opacity: 0.8;
    }
    100% {
      transform: scale(0.3) translate(-400px, -200px);
      opacity: 0;
    }
  }
  
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

customElements.define('build-a-box', BuildABox);
